/**
 * Generates widths between the first latest value and the `max` value,
 * by increasing the latest value with 8% until it's greater or the same
 * as `max`.
 */
export const generateWidths = (
	widths: [number, ...number[]] | number = 100,
	max = 8192,
	tolerance = 1.08,
): [number, ...number[]] => {
	// if `widths` is not an array make it into one.
	const currentWidths: [number, ...number[]] = Array.isArray(widths)
		? widths
		: [widths];
	const min = currentWidths.at(-1); // select last value

	if (min === undefined || min >= max) return currentWidths;

	// if the `newWidth` will exceed the max value, set it to max.
	const newWidth = min * tolerance > max ? max : Math.round(min * tolerance);

	return generateWidths([...currentWidths, newWidth], max, tolerance);
};

/**
 * Gets the image dimensions from a storyblok url
 */
export const getDimensionFromUrl = (src: string): [number, number] => {
	const { width, height } = /(?<width>\d+)x(?<height>\d+)/.exec(src)
		?.groups ?? { width: undefined, height: undefined };

	return [width, height].map(Number) as [number, number];
};

/**
 * Creates a functions that builds a storyblok image service url based on the width
 */
export const createSrcsetUrlTemplate =
	({
		src,
		aspectRatio,
		filters = "",
	}: {
		src: string;
		aspectRatio: number;
		filters?: string;
	}) =>
	(width: number): string => {
		const height = Math.round(width * aspectRatio);

		return `${src}/m/${width}x${height}/smart/${filters} ${width}w`;
	};
